import { useState } from "react";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import { 
  FileText, Zap, Users, ArrowRight, CheckCircle, Send, Bell, 
  CreditCard, Clock, X, Mail, Lock, User, Globe, Github, Apple,
  Check, Loader2
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const LandingPage = () => {
  const { login, user } = useAuth();
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [authTab, setAuthTab] = useState("login");
  const [loading, setLoading] = useState(false);
  
  // Form states
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [signupName, setSignupName] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [forgotEmail, setForgotEmail] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const features = [
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Lightning Fast",
      description: "Create professional invoices in under 60 seconds"
    },
    {
      icon: <FileText className="w-6 h-6" />,
      title: "PDF Export",
      description: "Download beautiful, professional PDF invoices instantly"
    },
    {
      icon: <Send className="w-6 h-6" />,
      title: "Email Invoices",
      description: "Send invoices directly to clients with PDF attachments"
    },
    {
      icon: <Bell className="w-6 h-6" />,
      title: "Smart Reminders",
      description: "Automated payment reminders for overdue invoices"
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: "Multi-Currency",
      description: "Invoice in USD, EUR, GBP, CAD, AUD and more"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Client Management",
      description: "Save client details for quick invoice creation"
    },
    {
      icon: <CreditCard className="w-6 h-6" />,
      title: "Online Payments",
      description: "Accept credit card payments via Stripe checkout"
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Recurring Invoices",
      description: "Automate weekly, monthly, or quarterly billing"
    }
  ];

  const pricingPlans = [
    {
      name: "Free",
      price: 0,
      description: "Perfect for getting started",
      features: [
        "5 invoices per month",
        "PDF invoice downloads",
        "Basic dashboard",
        "Manual payment tracking"
      ],
      limitations: [
        "No custom logo",
        "No automated reminders",
        "No email sending"
      ],
      cta: "Start Free",
      popular: false
    },
    {
      name: "Pro",
      price: 9,
      description: "For growing businesses",
      features: [
        "Unlimited invoices",
        "Custom invoice branding",
        "Email invoice sending",
        "Automated payment reminders",
        "Stripe payment collection",
        "Multi-currency support",
        "Recurring invoices",
        "Advanced dashboard insights"
      ],
      limitations: [],
      cta: "Start Pro Trial",
      popular: true
    }
  ];

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, {
        email: loginEmail,
        password: loginPassword
      }, { withCredentials: true });
      
      toast.success(`Welcome back, ${res.data.name}!`);
      setShowAuthDialog(false);
      window.location.href = '/dashboard';
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/signup`, {
        name: signupName,
        email: signupEmail,
        password: signupPassword
      });
      
      toast.success("Account created! Please check your email to verify.");
      setAuthTab("login");
      setSignupName("");
      setSignupEmail("");
      setSignupPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, {
        email: forgotEmail
      });
      toast.success("If this email exists, a reset link has been sent.");
      setShowForgotPassword(false);
      setForgotEmail("");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = (provider) => {
    if (provider === "google") {
      login();
    } else {
      toast.info(`${provider} login coming soon!`);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Billuna
              </span>
            </div>
            <div className="flex items-center gap-4">
              <a href="#pricing" className="text-sm font-medium text-muted-foreground hover:text-foreground hidden sm:block">
                Pricing
              </a>
              {user ? (
                <Button 
                  data-testid="go-to-dashboard-btn"
                  onClick={() => window.location.href = '/dashboard'}
                  className="bg-primary hover:bg-primary/90"
                >
                  Go to Dashboard
                </Button>
              ) : (
                <Button 
                  data-testid="login-btn"
                  onClick={() => setShowAuthDialog(true)}
                  className="bg-primary hover:bg-primary/90 btn-hover-lift"
                >
                  Sign In
                </Button>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-20 pb-32 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fadeIn">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
                <Zap className="w-4 h-4" />
                Simple invoicing. Faster payments.
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground tracking-tight mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Create invoices.<br />
                Get paid <span className="text-primary">faster</span>.
              </h1>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed max-w-lg">
                Billuna helps freelancers create professional invoices, send them to clients, accept online payments, and track everything with smart automated reminders.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Button 
                  data-testid="get-started-btn"
                  onClick={() => setShowAuthDialog(true)}
                  size="lg"
                  className="bg-primary hover:bg-primary/90 text-lg px-8 btn-hover-lift"
                >
                  Start Free
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  variant="outline"
                  size="lg"
                  onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}
                >
                  View Pricing
                </Button>
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-2">
                {["No credit card required", "5 free invoices/month", "Cancel anytime"].map((benefit, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    {benefit}
                  </div>
                ))}
              </div>
            </div>
            <div className="relative animate-fadeIn" style={{ animationDelay: '0.1s' }}>
              <div className="invoice-paper p-8 rounded-xl">
                <div className="flex justify-between items-start mb-8">
                  <div>
                    <h3 className="text-2xl font-bold text-primary mb-1">INVOICE</h3>
                    <p className="text-sm text-muted-foreground">#INV-0001</p>
                  </div>
                  <div className="text-right text-sm text-muted-foreground">
                    <p>Dec 15, 2025</p>
                    <p>Due: Dec 30, 2025</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-8 mb-8">
                  <div>
                    <p className="text-xs font-bold uppercase text-muted-foreground mb-2">From</p>
                    <p className="font-semibold">Your Business</p>
                    <p className="text-sm text-muted-foreground">123 Main Street</p>
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase text-muted-foreground mb-2">Bill To</p>
                    <p className="font-semibold">Client Name</p>
                    <p className="text-sm text-muted-foreground">client@email.com</p>
                  </div>
                </div>
                <div className="border-t border-b border-border py-4 mb-4">
                  <div className="flex justify-between text-sm font-medium text-muted-foreground mb-3">
                    <span>Description</span>
                    <span>Amount</span>
                  </div>
                  <div className="flex justify-between mb-2">
                    <span>Web Development</span>
                    <span className="font-mono">$2,500.00</span>
                  </div>
                  <div className="flex justify-between">
                    <span>UI/UX Design</span>
                    <span className="font-mono">$1,500.00</span>
                  </div>
                </div>
                <div className="flex justify-end">
                  <div className="text-right">
                    <div className="flex justify-between gap-8 text-sm mb-1">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span className="font-mono">$4,000.00</span>
                    </div>
                    <div className="flex justify-between gap-8 text-lg font-bold text-primary">
                      <span>Total</span>
                      <span className="font-mono">$4,000.00</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute -z-10 inset-0 bg-gradient-to-r from-primary/5 to-primary/10 blur-3xl rounded-full transform -rotate-6 scale-110"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-muted/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-semibold text-foreground mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Everything you need to get paid faster
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Powerful features designed for freelancers and small businesses
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, i) => (
              <Card 
                key={i} 
                className="bg-white border border-border rounded-xl p-6 card-hover"
                data-testid={`feature-card-${i}`}
              >
                <CardContent className="p-0">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center text-primary mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-semibold text-foreground mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Simple, transparent pricing
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Start free, upgrade when you need more
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {pricingPlans.map((plan, i) => (
              <Card 
                key={i} 
                className={`relative ${plan.popular ? 'border-primary border-2 shadow-lg' : 'border-border'}`}
                data-testid={`pricing-${plan.name.toLowerCase()}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-primary text-white text-xs font-semibold px-3 py-1 rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}
                <CardHeader className="text-center pb-2">
                  <CardTitle className="text-2xl">{plan.name}</CardTitle>
                  <CardDescription>{plan.description}</CardDescription>
                  <div className="mt-4">
                    <span className="text-4xl font-bold">${plan.price}</span>
                    <span className="text-muted-foreground">/month</span>
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, j) => (
                      <li key={j} className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                    {plan.limitations.map((limitation, j) => (
                      <li key={`limit-${j}`} className="flex items-start gap-2 text-muted-foreground">
                        <X className="w-5 h-5 flex-shrink-0 mt-0.5" />
                        <span className="text-sm">{limitation}</span>
                      </li>
                    ))}
                  </ul>
                  <Button 
                    className={`w-full ${plan.popular ? 'bg-primary hover:bg-primary/90' : ''}`}
                    variant={plan.popular ? 'default' : 'outline'}
                    onClick={() => setShowAuthDialog(true)}
                    data-testid={`pricing-cta-${plan.name.toLowerCase()}`}
                  >
                    {plan.cta}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-muted/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-semibold text-foreground mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              How Billuna Works
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Get paid in 4 simple steps
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: "1", title: "Create Invoice", desc: "Fill in your business details, client info, and line items" },
              { step: "2", title: "Send to Client", desc: "Email the invoice directly or share a payment link" },
              { step: "3", title: "Client Pays Online", desc: "Clients pay instantly via secure Stripe checkout" },
              { step: "4", title: "Track & Get Paid", desc: "Auto-update status with payment confirmations" }
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-12 h-12 bg-primary text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-semibold text-foreground mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Ready to simplify your invoicing?
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-xl mx-auto">
            Join thousands of freelancers who save time and get paid faster with Billuna.
          </p>
          <Button 
            data-testid="cta-get-started-btn"
            onClick={() => setShowAuthDialog(true)}
            size="lg"
            className="bg-primary hover:bg-primary/90 text-lg px-8 btn-hover-lift"
          >
            Get Started Free
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-primary rounded flex items-center justify-center">
                <FileText className="w-4 h-4 text-white" />
              </div>
              <span className="font-semibold">Billuna</span>
            </div>
            <p className="text-sm text-muted-foreground">
              © 2025 Billuna. Simple invoicing. Faster payments.
            </p>
          </div>
        </div>
      </footer>

      {/* Auth Dialog */}
      <Dialog open={showAuthDialog} onOpenChange={setShowAuthDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center text-2xl">
              {showForgotPassword ? "Reset Password" : "Welcome to Billuna"}
            </DialogTitle>
            <DialogDescription className="text-center">
              {showForgotPassword 
                ? "Enter your email to receive a reset link"
                : "Create invoices, get paid faster"
              }
            </DialogDescription>
          </DialogHeader>

          {showForgotPassword ? (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <Label htmlFor="forgot-email">Email</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Send Reset Link
              </Button>
              <Button 
                type="button" 
                variant="ghost" 
                className="w-full"
                onClick={() => setShowForgotPassword(false)}
              >
                Back to login
              </Button>
            </form>
          ) : (
            <>
              {/* Social Login Buttons */}
              <div className="space-y-3">
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => handleSocialLogin("google")}
                  data-testid="google-login-btn"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => handleSocialLogin("microsoft")}
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path fill="#F25022" d="M1 1h10v10H1z"/>
                    <path fill="#00A4EF" d="M1 13h10v10H1z"/>
                    <path fill="#7FBA00" d="M13 1h10v10H13z"/>
                    <path fill="#FFB900" d="M13 13h10v10H13z"/>
                  </svg>
                  Continue with Microsoft
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => handleSocialLogin("apple")}
                >
                  <Apple className="w-5 h-5 mr-2" />
                  Continue with Apple
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => handleSocialLogin("github")}
                >
                  <Github className="w-5 h-5 mr-2" />
                  Continue with GitHub
                </Button>
              </div>

              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">
                    Or continue with email
                  </span>
                </div>
              </div>

              <Tabs value={authTab} onValueChange={setAuthTab}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="login">Login</TabsTrigger>
                  <TabsTrigger value="signup">Sign Up</TabsTrigger>
                </TabsList>
                
                <TabsContent value="login">
                  <form onSubmit={handleEmailLogin} className="space-y-4">
                    <div>
                      <Label htmlFor="login-email">Email</Label>
                      <Input
                        id="login-email"
                        type="email"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        placeholder="you@example.com"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="login-password">Password</Label>
                      <Input
                        id="login-password"
                        type="password"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        placeholder="••••••••"
                        required
                      />
                    </div>
                    <Button type="submit" className="w-full" disabled={loading}>
                      {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                      Login with Email
                    </Button>
                    <Button 
                      type="button" 
                      variant="link" 
                      className="w-full text-sm"
                      onClick={() => setShowForgotPassword(true)}
                    >
                      Forgot password?
                    </Button>
                  </form>
                </TabsContent>
                
                <TabsContent value="signup">
                  <form onSubmit={handleEmailSignup} className="space-y-4">
                    <div>
                      <Label htmlFor="signup-name">Name</Label>
                      <Input
                        id="signup-name"
                        type="text"
                        value={signupName}
                        onChange={(e) => setSignupName(e.target.value)}
                        placeholder="John Doe"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="signup-email">Email</Label>
                      <Input
                        id="signup-email"
                        type="email"
                        value={signupEmail}
                        onChange={(e) => setSignupEmail(e.target.value)}
                        placeholder="you@example.com"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="signup-password">Password</Label>
                      <Input
                        id="signup-password"
                        type="password"
                        value={signupPassword}
                        onChange={(e) => setSignupPassword(e.target.value)}
                        placeholder="Min 8 characters"
                        required
                        minLength={8}
                      />
                    </div>
                    <Button type="submit" className="w-full" disabled={loading}>
                      {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <User className="w-4 h-4 mr-2" />}
                      Create Account
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandingPage;
